// Small hand-rolled SVG charts for the report dashboard. Each mark opens the comments behind it, shows its numbers on
// hover and keyboard focus, and each chart has a table twin (Card). Colours are validated tokens in styles.css (REPORT
// DASHBOARD region); text never wears a data colour.
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { OPINION, fmtDate, nf, pct } from './lib.jsx';

/** The element's width in CSS pixels, so an SVG can be drawn 1:1 and its text keeps its real size on any screen. */
function useWidth(initial) {
  const ref = useRef(null);
  const [w, setW] = useState(initial);
  useLayoutEffect(() => {
    const set = (px) => setW(Math.max(260, Math.round(px)));
    set(ref.current.getBoundingClientRect().width); // before the first paint, so it never draws at the wrong scale
    const ro = new ResizeObserver(([e]) => set(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// Categorical slots: the five pass the CVD and normal-vision floors all-pairs, so any subset (and OTHER) stays apart.
// Colour follows the entity: callers map an entity to a fixed slot, never to its rank.
export const CAT = ['var(--c1)', 'var(--c2)', 'var(--c3)', 'var(--c4)', 'var(--c5)'];
export const OTHER = 'var(--c-other)';
/** A one-hue ordinal ramp, lightest first, for ordered stages (collection funnel, context levels, market tiers). */
export const ORD = ['var(--o1)', 'var(--o2)', 'var(--o3)'];

/** A round axis maximum at or above v whose half (the middle tick) is a whole number. */
export function niceMax(v) {
  const p = 10 ** Math.floor(Math.log10(Math.max(1, v)));
  return [1, 1.5, 2, 3, 4, 5, 6, 8, 10].map((m) => m * p).find((m) => m >= v && Number.isInteger(m / 2));
}

/** Highest total first, whatever order arrives; the sort is stable, so the server's tie-breaks survive. */
export const byTotal = (list) => [...(list ?? [])].sort((a, b) => b.total - a.total);

/** Sentiment cells ({positive: {n, key}, …}) as parts in bar order. */
export const sentSegs = (cells, list = OPINION) => list.map((s) => ({ k: s.key, label: s.label, color: s.color, hatch: s.hatch, n: cells?.[s.key]?.n ?? 0, key: cells?.[s.key]?.key }));
const total = (xs) => xs.reduce((a, x) => a + x.n, 0);

/** A dashboard card: a question as the title, the denominator, then the chart (or why there is none) and its table twin. */
export function Card({ title, denom, span = 6, order, note, table, children, foot }) {
  const [asTable, setAsTable] = useState(false);
  const id = useId();
  return (
    <section className={`card span-${span}`} style={order ? { order } : undefined} aria-labelledby={id}>
      <div className="card-h">
        <h3 id={id}>{title}</h3>
        {table && children ? <button type="button" className="btn ghost small" aria-pressed={asTable} onClick={() => setAsTable(!asTable)}>{asTable ? 'Show chart' : 'Show table'}</button> : null}
      </div>
      {denom ? <p className="card-d">{denom}</p> : null}
      {note ? <p className="card-note">{note}</p> : null}
      {asTable || !children ? table : children}
      {foot}
    </section>
  );
}

/** A stat tile: label, value, one line of context. A button when its number is a count of comments. */
export function Kpi({ label, value, sub, onClick, text }) {
  const body = <><span className="k-l">{label}</span><span className={`k-v${text ? ' text' : ''}`}>{value}</span>{sub ? <span className="k-s">{sub}</span> : null}</>;
  return onClick ? <button type="button" className="kpi-tile" onClick={onClick}>{body}</button> : <div className="kpi-tile">{body}</div>;
}

/** Swatch and label per part, for charts whose counts sit on each row. */
export function Legend({ items = OPINION.map((s) => ({ k: s.key, label: s.label, color: s.color, hatch: s.hatch })) }) {
  return <ul className="legend" aria-hidden="true">{items.map((x) => <li key={x.k}><i className={x.hatch ? 'hatch' : undefined} style={{ backgroundColor: x.color }} />{x.label}</li>)}</ul>;
}

const TAU = 2 * Math.PI, R = 64;
const at = (a) => [84 + R * Math.sin(a), 84 - R * Math.cos(a)].map((v) => v.toFixed(2)).join(' ');
const arc = (a0, a1) => `M${at(a0)}A${R} ${R} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${at(a1)}`;

/**
 * Part-to-whole for a partition where each comment counts once (6 parts at most). The total sits in the centre and the
 * hovered or focused part's share takes its place; the legend reads "74% (87) Positive". Parts and legend open the comments.
 */
export function Donut({ items, unit = 'comments', label, onPick }) {
  const [active, setActive] = useState(null);
  const shown = items.filter((x) => x.n > 0);
  const sum = total(shown);
  const gap = shown.length > 1 ? 2.5 / R : 0; // a 2px surface gap between parts
  let a = 0;
  const arcs = shown.map((x) => { const a0 = a; a += (x.n / sum) * TAU; return { x, d: arc(a0 + gap / 2, a - gap / 2) }; });
  const cur = shown.find((x) => x.k === active);
  const hover = (k) => ({ onMouseEnter: () => setActive(k), onMouseLeave: () => setActive(null), onFocus: () => setActive(k), onBlur: () => setActive(null) });
  return (
    <div className="donut">
      <svg viewBox="0 0 168 168" aria-hidden="true">
        {shown.length === 1 ? <circle cx="84" cy="84" r={R} stroke={shown[0].color} onClick={() => onPick?.(shown[0])} {...hover(shown[0].k)} />
          : arcs.map(({ x, d }) => <path key={x.k} d={d} stroke={x.color} className={active && active !== x.k ? 'dim' : ''} onClick={() => onPick?.(x)} {...hover(x.k)} />)}
        <text x="84" y="88" className="d-val">{cur ? pct(cur.n / sum) : nf.format(sum)}</text>
        <text x="84" y="108" className="d-cap">{cur ? `${nf.format(cur.n)} ${cur.n === 1 ? unit.replace(/s$/, '') : unit}` : unit}</text>
      </svg>
      <ul className="d-legend" aria-label={label}>
        {shown.map((x) => (
          <li key={x.k}>
            <button type="button" className={active === x.k ? 'on' : ''} onClick={() => onPick?.(x)} {...hover(x.k)} title={x.title}
              aria-label={`${x.label}: ${nf.format(x.n)} ${unit}, ${pct(x.n / sum)}`}>
              <i style={{ background: x.color }} /><b>{pct(x.n / sum)}</b><span className="n">({nf.format(x.n)})</span><span className="l">{x.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One bar split into parts with 2px surface gaps; `scale` (0–1) shortens it for ranked rows. Each part opens its comments. */
export function StackBar({ segs, label, scale = 1, onPick }) {
  const [tip, setTip] = useState(null);
  // Largest part first, so each bar reads left to right by size; colours stay with the sentiment, not the rank.
  const shown = segs.filter((s) => s.n > 0).sort((a, b) => b.n - a.n);
  const sum = total(shown);
  const show = (s) => (e) => setTip({ s, x: e.currentTarget.offsetLeft + e.currentTarget.offsetWidth / 2 });
  return (
    <div className="sbar" onMouseLeave={() => setTip(null)}>
      <div className="sbar-fill" style={{ width: `${Math.max(scale * 100, 0.5)}%` }}>
        {shown.map((s) => (
          <button key={s.k} type="button" className={s.hatch ? 'hatch' : undefined} style={{ flexGrow: s.n, backgroundColor: s.color }} onClick={() => onPick?.(s)} onMouseEnter={show(s)} onFocus={show(s)} onBlur={() => setTip(null)}
            aria-label={`${label}: ${s.label}, ${nf.format(s.n)} of ${nf.format(sum)} (${pct(s.n / sum)})`} />
        ))}
      </div>
      {tip ? <div className="tip" style={{ left: tip.x, top: 0 }}><b>{nf.format(tip.s.n)}</b> {tip.s.label} · {pct(tip.s.n / sum)} of {nf.format(sum)}</div> : null}
    </div>
  );
}

/** Ranked bars for one measure, in the order given, value at the end of the row. A row opens its comments. */
export function RankBars({ rows, onPick, rank = false, color = 'var(--c1)' }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ol className={`rbars${rank ? ' ranked' : ''}`}>
      {rows.map((r, i) => (
        <li key={r.id}>
          <button type="button" className="rbar" disabled={!onPick || !r.n} onClick={() => onPick(r)} title={r.title} aria-label={r.title ? `${r.label}: ${r.title}` : undefined}>
            {rank ? <span className="rk">{i + 1}</span> : null}
            <span className="rl">{r.label}</span>
            <span className="rt"><span style={{ width: `${(r.value / max) * 100}%`, background: color }} /></span>
            <span className="rv">{nf.format(r.value)}</span>
          </button>
        </li>
      ))}
    </ol>
  );
}

/** Ranked rows whose bar length is the row's count and whose parts are its sentiment mix (themes, which overlap). */
export function MixBars({ rows, onPick }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <ol className="mixbars">
      {rows.map((r) => (
        <li key={r.id}>
          <div className="mb-head">
            <button type="button" className="linklike mb-name" onClick={() => onPick(r, null)}>{r.label}</button>
            <span className="mb-val">{nf.format(r.n)}</span>
          </div>
          {r.sub ? <p className="mb-sub">{r.sub}</p> : null}
          <StackBar segs={sentSegs(r.sentiment)} label={r.label} scale={r.n / max} onPick={(s) => onPick(r, s)} />
        </li>
      ))}
    </ol>
  );
}

/** The table twin of a part-to-whole chart: one row per part, its count (opens the comments) and its share. `counts`: no share column (too few for one). */
export function ShareTable({ head, rows, unit = 'Comments', onPick, extra, counts }) {
  const sum = total(rows);
  return (
    <div className="table-wrap"><table className="data">
      <thead><tr><th>{head}</th>{extra ? <th className="num">{extra.head}</th> : null}<th className="num">{unit}</th>{counts ? null : <th className="num">Share</th>}</tr></thead>
      <tbody>{rows.map((x) => (
        <tr key={x.k}>
          <td>{x.color ? <i className="sw" style={{ background: x.color }} /> : null}{x.label}</td>
          {extra ? <td className="num">{nf.format(extra.value(x))}</td> : null}
          <td className="num"><Count n={x.n} onPick={onPick && x.key ? () => onPick(x) : null} /></td>
          {counts ? null : <td className="num">{sum ? pct(x.n / sum) : '–'}</td>}
        </tr>
      ))}</tbody>
    </table></div>
  );
}

/** The table twin of sentiment-mix rows: a row's total and each sentiment, every count opening its comments. */
export function SentTable({ head, rows, onPick }) {
  return (
    <div className="table-wrap"><table className="data">
      <thead><tr><th>{head}</th><th className="num">Comments</th>{OPINION.map((s) => <th key={s.key} className="num">{s.label}</th>)}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.id}>
          <td>{r.label}</td>
          <td className="num"><Count n={r.n} onPick={() => onPick(r, null)} /></td>
          {sentSegs(r.sentiment).map((s) => <td key={s.k} className="num"><Count n={s.n} onPick={() => onPick(r, s)} /></td>)}
        </tr>
      ))}</tbody>
    </table></div>
  );
}

export const Count = ({ n, onPick }) => (n && onPick ? <button type="button" className="linklike" onClick={onPick}>{nf.format(n)}</button> : <span className={n ? '' : 'muted'}>{nf.format(n)}</span>);

const DAY = 864e5;
const utc = (d) => Date.parse(`${d.slice(0, 10)}T00:00:00Z`);

/**
 * Stacked columns on a real time axis (the searched dates). Only observed buckets are drawn: no invented zeroes, gaps
 * stay gaps. `buckets`: [{d, n, key?, parts: [{k, label, color, n, key?}]}]. A crosshair snaps to the nearest bucket;
 * its tooltip lists every part. Columns open their comments when they have a key.
 */
export function TimeColumns({ buckets, bucket, from, to, label, onPick }) {
  const [hi, setHi] = useState(null);
  const [box, W] = useWidth(720);
  const step = bucket === 'week' ? 7 * DAY : DAY;
  const ts = buckets.map((b) => utc(b.d));
  const t0 = Math.min(utc(from), ...ts), t1 = Math.max(utc(to), ...ts) + step;
  const H = W < 480 ? 170 : 200, L = 34, Rm = 8, T = 10, B = 24;
  const x = (t) => L + ((t - t0) / (t1 - t0)) * (W - L - Rm);
  const bw = Math.max(3, Math.min(24, x(t0 + step) - x(t0) - 2));
  const max = niceMax(Math.max(1, ...buckets.map((b) => b.n)));
  const y = (v) => T + (1 - v / max) * (H - T - B);
  const days = (t1 - t0) / DAY;
  const every = Math.max(days <= 14 ? 2 : days <= 62 ? 7 : days <= 180 ? 28 : 56, Math.ceil(days / Math.max(2, Math.floor(W / 90)))) * DAY;
  const xticks = []; for (let t = t0; t < t1 - every / 3; t += every) xticks.push(t);
  const cx = (i) => x(ts[i]) + (x(ts[i] + step) - x(ts[i])) / 2;
  const fmt = (d) => fmtDate(d, { day: 'numeric', month: 'short' });
  const name = (b) => `${bucket === 'week' ? 'Week of ' : ''}${fmt(b.d)}`;
  function move(e) {
    const r = e.currentTarget.getBoundingClientRect(), px = ((e.clientX - r.left) / r.width) * W;
    let best = null;
    buckets.forEach((_, i) => { if (best === null || Math.abs(cx(i) - px) < Math.abs(cx(best) - px)) best = i; });
    setHi(best);
  }
  const open = (b) => b.key && onPick?.(b);
  const cur = hi === null ? null : buckets[hi];
  return (
    <div className="tcols" ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} role="group" aria-label={label} onMouseMove={move} onMouseLeave={() => setHi(null)} onClick={() => cur && open(cur)}>
        {[0, max / 2, max].map((v) => <g key={v}><line x1={L} x2={W - Rm} y1={y(v)} y2={y(v)} className={v ? 'grid' : 'base'} /><text x={L - 6} y={y(v) + 4} className="ytick">{nf.format(v)}</text></g>)}
        {xticks.map((t) => <text key={t} x={x(t)} y={H - 6} className="xtick">{fmt(new Date(t).toISOString())}</text>)}
        {hi !== null ? <line x1={cx(hi)} x2={cx(hi)} y1={T} y2={H - B} className="cross" /> : null}
        {buckets.map((b, i) => {
          let acc = 0;
          const parts = b.parts.filter((p) => p.n > 0);
          return (
            <g key={b.d} className={`tcol${b.key ? ' go' : ''}`} tabIndex={b.key ? 0 : -1} role={b.key ? 'button' : undefined} onFocus={() => setHi(i)} onBlur={() => setHi(null)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(b); } }}
              aria-label={`${name(b)}: ${nf.format(b.n)} ${parts.length > 1 ? `(${parts.map((p) => `${p.label} ${p.n}`).join(', ')})` : ''}`}>
              {parts.map((p, j) => {
                const top = j === parts.length - 1, y0 = y(acc), y1 = y(acc + p.n);
                acc += p.n;
                const h = Math.max(1, y0 - y1 - (top ? 0 : 2)), left = cx(i) - bw / 2;
                if (!top) return <rect key={p.k} x={left} y={y1 + 2} width={bw} height={h} fill={p.color} />;
                const r = Math.min(4, bw / 2, h);
                return <path key={p.k} fill={p.color} d={`M${left} ${y0}V${y0 - h + r}Q${left} ${y0 - h} ${left + r} ${y0 - h}H${left + bw - r}Q${left + bw} ${y0 - h} ${left + bw} ${y0 - h + r}V${y0}Z`} />;
              })}
            </g>
          );
        })}
      </svg>
      {cur ? (
        <div className="tip" style={{ left: `${(cx(hi) / W) * 100}%`, top: 4 }}>
          <b>{nf.format(cur.n)}</b> · {name(cur)}
          {cur.parts.length > 1 ? cur.parts.filter((p) => p.n).map((p) => <div className="row" key={p.k}><span><i className="key" style={{ background: p.color }} />{p.label}</span><span>{nf.format(p.n)}</span></div>) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Themes as bubbles: x = comments, y = net sentiment (positive minus negative over both), area = posts. Quadrants split
 * at the average theme size and at zero. Themes overlap, so bubbles are not parts of a whole. One colour, numbered by rank.
 */
export function Quadrant({ points, onPick }) {
  const [hi, setHi] = useState(null);
  const [box, W] = useWidth(640);
  const narrow = W < 560; // corner labels go on two lines so the top two never meet
  const H = Math.round(Math.min(360, Math.max(280, W * 0.45))), L = 46, Rm = 26, T = narrow ? 38 : 26, B = 42;
  const xmax = niceMax(Math.max(...points.map((p) => p.n)));
  const x = (v) => L + (v / xmax) * (W - L - Rm);
  const y = (v) => T + ((1.3 - v) / 2.6) * (H - T - B); // ±1.3: bubbles at ±100 stay clear of the corner labels
  const pmax = Math.max(1, ...points.map((p) => p.posts));
  const rad = (p) => 10 + 12 * Math.sqrt(p.posts / pmax);
  const xmid = total(points) / points.length;
  const cur = points.find((p) => p.id === hi);
  const corner = [[W - Rm - 4, T - 10, 'end', 'Much discussed, more positive'], [L + 6, T - 10, 'start', 'Less discussed, more positive'],
    [W - Rm - 4, H - B - 8, 'end', 'Much discussed, more negative'], [L + 6, H - B - 8, 'start', 'Less discussed, more negative']];
  return (
    <div className="quad" ref={box}>
      <svg viewBox={`0 0 ${W} ${H}`} role="group" aria-label="Themes by size and net sentiment">
        {corner.map(([cx, cy, anchor, text]) => (
          <text key={text} x={cx} y={narrow && cy < T ? cy - 12 : cy} textAnchor={anchor} className="q-corner">
            {narrow ? text.split(', ').map((part, i) => <tspan key={part} x={cx} dy={i ? 13 : narrow && cy > T ? -13 : 0}>{part}</tspan>) : text}
          </text>
        ))}
        <line x1={L} x2={W - Rm} y1={H - B} y2={H - B} className="base" />
        <line x1={L} x2={L} y1={T} y2={H - B} className="base" />
        <line x1={L} x2={W - Rm} y1={y(0)} y2={y(0)} className="mid" />
        <line x1={x(xmid)} x2={x(xmid)} y1={T} y2={H - B} className="mid" />
        {[0, xmax / 2, xmax].map((v) => <text key={v} x={x(v)} y={H - B + 16} className="xtick">{nf.format(v)}</text>)}
        {[[1, '+100'], [0, '0'], [-1, '−100']].map(([v, l]) => <g key={v}>{v ? <line x1={L} x2={W - Rm} y1={y(v)} y2={y(v)} className="grid" /> : null}<text x={L - 8} y={y(v) + 4} className="ytick">{l}</text></g>)}
        <text x={(L + W - Rm) / 2} y={H - 6} className="xtick">Comments in the theme</text>
        <text x={12} y={(T + H - B) / 2} className="xtick" transform={`rotate(-90 12 ${(T + H - B) / 2})`}>Net sentiment</text>
        {[...points].sort((a, b) => b.posts - a.posts).map((p) => (
          <circle key={p.id} cx={x(p.n)} cy={y(p.net)} r={rad(p)} className={`bub${hi !== null && hi !== p.id ? ' dim' : ''}`} tabIndex={0} role="button"
            aria-label={`${p.rank}. ${p.label}: ${nf.format(p.n)} comments on ${nf.format(p.posts)} posts, net ${signed(p.net)}`}
            onMouseEnter={() => setHi(p.id)} onMouseLeave={() => setHi(null)} onFocus={() => setHi(p.id)} onBlur={() => setHi(null)} onClick={() => onPick(p)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(p); } }} />
        ))}
        {points.map((p) => <text key={p.id} x={x(p.n)} y={y(p.net) + 4} className="b-n">{p.rank}</text>)}
      </svg>
      {cur ? (
        <div className="tip" style={{ left: `${(x(cur.n) / W) * 100}%`, top: `${((y(cur.net) - rad(cur)) / H) * 100}%` }}>
          <b>{cur.rank}. {cur.label}</b>
          <div className="row"><span>Comments</span><span>{nf.format(cur.n)}</span></div>
          <div className="row"><span>Posts</span><span>{nf.format(cur.posts)}</span></div>
          <div className="row"><span>Net sentiment</span><span>{signed(cur.net)} ({nf.format(cur.pos)} positive, {nf.format(cur.neg)} negative)</span></div>
        </div>
      ) : null}
    </div>
  );
}

/** Net sentiment (−1 to 1) as a whole number from −100 to +100. */
export const signed = (v) => { if (v === null || v === undefined) return '–'; const n = Math.round(v * 100); return n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '0'; };
